import React, { useState, useEffect, useCallback } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Card,
  CardActionArea,
  CardMedia,
  CardContent,
  CardActions,
  Typography,
  Box,
  Chip,
  Avatar,
  Tooltip,
  Skeleton,
  IconButton,
} from "@mui/material";
import { Person as PersonIcon } from "@mui/icons-material";
import StarRating from "../Rating/StarRating";
import VibeCheckBar, { ReactionCounts } from "../Rating/VibeCheckBar";
import LikeButton from "./LikeButton";
import ShareMenu from "./ShareMenu";
import TryOnButton from "./TryOnButton";
import TryOnModal from "./TryOnModal";
import QuickActionOverlay from "./QuickActionOverlay";
import StyleScoreBadge from "./StyleScoreBadge";
import AffiliateLink from "./AffiliateLink";
import AvatarCluster from "../Social/AvatarCluster";
import { Outfit, AffiliateData } from "../../types";
import UserService, { UserProfile } from "../../services/user-service";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import { submitReaction } from "../../services/reactionsService";
import SavedOutfitsService from "../../services/saved-outfits-service";

interface OutfitCardProps {
  outfit: Outfit;
  showUserInfo?: boolean;
  userInfo?: {
    id: string;
    username: string;
    displayName: string;
    profilePicture?: string;
    isVerified?: boolean;
  };
  onUserClick?: (userId: string) => void;
  onSaveChange?: (outfitId: string, saved: boolean) => void;
  onLikeChange?: (outfitId: string, liked: boolean) => void;
  affiliateData?: AffiliateData[];
  recentRaters?: Array<{ id: string; profilePicture?: string; displayName?: string }>;
  totalRaterCount?: number;
}

/**
 * Outfit card component for displaying outfit previews in grids
 */
const OutfitCard: React.FC<OutfitCardProps> = ({ 
  outfit, 
  showUserInfo = true, 
  userInfo: propUserInfo,
  onUserClick,
  onSaveChange,
  onLikeChange,
  affiliateData,
  recentRaters = [],
  totalRaterCount = 0,
}) => {
  const navigate = useNavigate();
  const cardRef = React.useRef<HTMLDivElement>(null);
  const { isAuthenticated, user: authUser, updateUser } = useAuth();
  const { showToast } = useToast();

  // Quick action overlay hover state — desktop only
  const [isHovered, setIsHovered] = useState(false);
  const isTouchDevice = typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0);

  // State
  const [isLiked, setIsLiked] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [userInfo, setUserInfo] = useState<UserProfile | null>(null);
  const [isLoadingUser, setIsLoadingUser] = useState(false);
  const [tryOnModalOpen, setTryOnModalOpen] = useState(false);

  // Vibe check reaction state — initializes from outfit prop
  const [localReactions, setLocalReactions] = useState<ReactionCounts>(
    () => outfit?.reactions ?? {}
  );
  const [localUserReaction, setLocalUserReaction] = useState<string | null>(
    () => outfit?.userReaction ?? null
  );

  // Fetch reaction counts from API on mount so they persist across page refreshes
  useEffect(() => {
    if (!outfit?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const { getReactions } = await import('../../services/reactionsService');
        const counts = await getReactions(outfit.id);
        if (!cancelled && counts) {
          setLocalReactions(counts);
        }
      } catch {
        // Silently fail — show whatever we have from props
      }
    })();
    return () => { cancelled = true; };
  }, [outfit?.id]);

  // Sync local reaction state when outfit prop changes
  useEffect(() => {
    if (outfit?.reactions && Object.keys(outfit.reactions).length > 0) {
      setLocalReactions(outfit.reactions);
    }
    if (outfit?.userReaction !== undefined) {
      setLocalUserReaction(outfit.userReaction ?? null);
    }
  }, [outfit?.reactions, outfit?.userReaction]);

  // Check saved status on mount for authenticated users
  useEffect(() => {
    if (isAuthenticated && authUser && outfit?.id) {
      const saved = SavedOutfitsService.checkSaved(outfit.id, authUser.savedOutfits || []);
      setIsSaved(saved);
    }
  }, [isAuthenticated, authUser, outfit?.id]);

  // Check liked status on mount from user's likedOutfits array
  useEffect(() => {
    if (isAuthenticated && authUser && outfit?.id) {
      const liked = (authUser.likedOutfits || []).includes(outfit.id);
      setIsLiked(liked);
    }
  }, [isAuthenticated, authUser, outfit?.id]);

  /**
   * Handle save toggle — optimistic UI with rollback on failure.
   */
  const handleSaveToggle = useCallback(async () => {
    if (!isAuthenticated) {
      showToast('Please sign in to save outfits', 'info');
      return;
    }

    const previousSaved = isSaved;
    setIsSaved(!isSaved);

    try {
      if (previousSaved) {
        const result = await SavedOutfitsService.unsaveOutfit(outfit.id);
        if (!result) throw new Error('Unsave failed');
        // Update AuthContext so other components see the change
        if (authUser) {
          updateUser({ savedOutfits: (authUser.savedOutfits || []).filter(id => id !== outfit.id) });
        }
        // Notify parent component
        if (onSaveChange) onSaveChange(outfit.id, false);
      } else {
        const result = await SavedOutfitsService.saveOutfit(outfit.id);
        if (!result) throw new Error('Save failed');
        // Update AuthContext so other components see the change
        if (authUser) {
          updateUser({ savedOutfits: [...(authUser.savedOutfits || []), outfit.id] });
        }
        // Notify parent component
        if (onSaveChange) onSaveChange(outfit.id, true);
      }
    } catch (error) {
      console.error('Error toggling save:', error);
      setIsSaved(previousSaved);
      showToast('Failed to update saved status. Please try again.', 'error');
    }
  }, [isSaved, isAuthenticated, outfit?.id, showToast, authUser, updateUser, onSaveChange]);

  /**
   * Handle vibe check reaction — always increments the count.
   * Anonymous users can react multiple times; counts are cumulative.
   * Optimistic UI update with rollback on API error.
   */
  const handleVibeReact = useCallback(async (emoji: string) => {
    const prevReactions = { ...localReactions };

    // Always increment — cumulative reactions
    const nextReactions = { ...localReactions };
    nextReactions[emoji] = (nextReactions[emoji] ?? 0) + 1;
    setLocalReactions(nextReactions);
    setLocalUserReaction(emoji);

    try {
      await submitReaction(outfit.id, emoji);
    } catch (error) {
      // Rollback on API failure
      setLocalReactions(prevReactions);
      showToast('Failed to submit reaction. Please try again.', 'error');
    }
  }, [localReactions, outfit?.id, showToast]);

  // Track outfit view when card is visible in viewport for 1+ second
  React.useEffect(() => {
    const el = cardRef.current;
    if (!el || !outfit?.id) return;

    let viewTimer: ReturnType<typeof setTimeout> | null = null;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          viewTimer = setTimeout(() => {
            import('../../services/ActivityTracker').then(m => m.default.trackOutfitView(outfit.id));
          }, 1000);
        } else if (viewTimer) {
          clearTimeout(viewTimer);
          viewTimer = null;
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(el);
    return () => { observer.disconnect(); if (viewTimer) clearTimeout(viewTimer); };
  }, [outfit?.id]);
  const [tryOnSessionId, setTryOnSessionId] = useState<string | null>(null);

  // Fetch user information if needed
  // Use a ref to track if we've already fetched to prevent repeated API calls
  const hasFetchedUserRef = React.useRef(false);
  
  useEffect(() => {
    const fetchUserInfo = async () => {
      // Skip if already fetched
      if (hasFetchedUserRef.current) {
        return;
      }
      
      // Skip if user info is provided via props, outfit.user exists, outfit has user data, or showUserInfo is false
      if (!outfit || !showUserInfo || !outfit.userId || outfit.user || propUserInfo || outfit.userDisplayName || outfit.userName) {
        return;
      }

      // Additional safety check to prevent hydration issues
      if (typeof window === 'undefined') {
        return;
      }

      hasFetchedUserRef.current = true;
      setIsLoadingUser(true);
      try {
        const user = await UserService.getUserById(outfit.userId);
        setUserInfo(user);
      } catch (error) {
        console.error('Failed to fetch user info:', error);
      } finally {
        setIsLoadingUser(false);
      }
    };

    // Small delay to prevent hydration mismatches
    const timer = setTimeout(fetchUserInfo, 100);
    return () => clearTimeout(timer);
  }, [outfit?.userId, outfit?.user, outfit?.userDisplayName, outfit?.userName, showUserInfo, propUserInfo]);

  // Safety check for outfit prop with debugging
  if (!outfit) {
    console.error('OutfitCard: No outfit data provided');
    return (
      <Card elevation={2} sx={{ height: "100%", backgroundColor: "#E3F2FD", color: "#1C1C1C" }}>
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            Outfit data not available
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // Debug logging for outfit data
  console.log('OutfitCard outfit data:', {
    id: outfit.id,
    title: outfit.title,
    description: outfit.description,
    createdAt: outfit.createdAt,
    updatedAt: outfit.updatedAt,
    eventType: outfit.eventType,
    hasId: !!outfit.id,
    idType: typeof outfit.id
  });

  // Format date with better error handling
  const formatDate = (date: string | Date | undefined) => {
    if (!date) return "Unknown date";
    try {
      const dateObj = new Date(date);
      // Check if date is valid
      if (isNaN(dateObj.getTime())) {
        console.warn('Invalid date received:', date);
        return "Invalid date";
      }
      return dateObj.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      console.error('Date formatting error:', error, 'Date value:', date);
      return "Invalid date";
    }
  };

  // Handle like change — update local state and AuthContext
  const handleLikeChange = (liked: boolean) => {
    setIsLiked(liked);
    // Update AuthContext so other components see the change
    if (authUser) {
      if (liked) {
        updateUser({ likedOutfits: [...(authUser.likedOutfits || []), outfit.id] });
      } else {
        updateUser({ likedOutfits: (authUser.likedOutfits || []).filter(id => id !== outfit.id) });
      }
    }
    // Notify parent component
    if (onLikeChange) onLikeChange(outfit.id, liked);
  };

  // Prevent event propagation for card actions
  const handleActionClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // Handle user click
  const handleUserClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (outfit.userId) {
      if (onUserClick) {
        onUserClick(outfit.userId);
      } else {
        navigate(`/profile/${outfit.userId}`);
      }
    }
  };

  // Handle try-on workflow
  const handleTryOnStart = () => {
    // Optional: Add any pre-processing logic here
  };

  const handleTryOnComplete = (resultImageUrl: string) => {
    // Optional: Add any post-processing logic here
    console.log('Try-on completed with result:', resultImageUrl);
  };

  const handleTryOnError = (error: string) => {
    // Optional: Add error handling logic here
    console.error('Try-on error:', error);
  };

  const handleOpenTryOnModal = (sessionId: string) => {
    setTryOnSessionId(sessionId);
    setTryOnModalOpen(true);
  };

  const handleCloseTryOnModal = () => {
    setTryOnModalOpen(false);
    setTryOnSessionId(null);
  };

  const handleDownloadTryOnResult = (imageUrl: string) => {
    // Create download link
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = `try-on-result-${outfit.id}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Get user display information
  
  // Safe user data access to prevent crashes
  const getSafeUserData = () => {
    const user = propUserInfo || outfit.user || userInfo;
    
    if (!user) {
      return {
        displayName: outfit.userDisplayName || outfit.userName || 'Anonymous User',
        profilePicture: outfit.userProfilePicture || null,
        isVerified: false,
        isLoading: false,
        id: outfit.userId || 'unknown'
      };
    }
    
    const userAny = user as any;
    return {
      displayName: user.displayName || userAny.email?.split('@')[0] || 'User',
      profilePicture: user.profilePicture || userAny.picture || null,
      isVerified: userAny.isVerified || false,
      isLoading: false,
      id: userAny.id || userAny.userId || 'unknown'
    };
  };

  const getUserDisplayInfo = () => {
    // User data priority: props > outfit.user > API fetched userInfo > fallback
    const user = propUserInfo || outfit.user || userInfo;
    
    if (user) {
      return {
        displayName: user.displayName || `User ${outfit.userId?.substring(0, 8)}` || null,
        profilePicture: user.profilePicture, // Use profile picture from user object
        isVerified: (user as any).isVerified || false,
        isLoading: false,
      };
    }

    // Check if outfit has user data directly (from backend)
    if (outfit.userDisplayName || outfit.userName) {
      return {
        displayName: outfit.userDisplayName || outfit.userName || `User ${outfit.userId?.substring(0, 8)}` || null,
        profilePicture: outfit.userProfilePicture || undefined, // CRITICAL: Use userProfilePicture from outfit
        isVerified: false,
        isLoading: false,
      };
    }

    if (isLoadingUser) {
      return {
        displayName: "Loading...",
        profilePicture: undefined,
        isVerified: false,
        isLoading: true,
      };
    }

    // Check if outfit has user data directly (from backend)
    if (outfit.userDisplayName || outfit.userName) {
      return {
        displayName: outfit.userDisplayName || outfit.userName || `User ${outfit.userId?.substring(0, 8)}` || null,
        profilePicture: outfit.userProfilePicture || undefined,
        isVerified: false,
        isLoading: false,
      };
    }

    if (isLoadingUser) {
      return {
        displayName: "Loading...",
        profilePicture: undefined,
        isVerified: false,
        isLoading: true,
      };
    }

    // Fallback for missing user information
    return {
      displayName: `User ${outfit.userId?.substring(0, 8)}` || null,
      profilePicture: undefined,
      isVerified: false,
      isLoading: false,
    };
  };

  const userDisplayInfo = getSafeUserData();

  return (
    <Card
      ref={cardRef}
      elevation={0}
      onMouseEnter={() => !isTouchDevice && setIsHovered(true)}
      onMouseLeave={() => !isTouchDevice && setIsHovered(false)}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        borderRadius: "16px",
        border: "none",
        boxShadow: "0 2px 12px rgba(0,0,0,0.06), 0 4px 24px rgba(0,0,0,0.1)",
        position: "relative",
        "@media (prefers-reduced-motion: no-preference)": {
          transition: "transform 200ms ease, box-shadow 200ms ease",
          "&:hover": {
            transform: "scale(1.02)",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          },
        },
      }}
    >
      <CardActionArea
        component={RouterLink}
        to={`/outfit/${outfit.id}`}
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
        }}
      >
        <Box
          sx={{
            position: "relative",
            aspectRatio: "3/4",
            overflow: "hidden",
            backgroundColor: "rgba(0, 0, 0, 0.04)",
          }}
        >
          {!isImageLoaded && (
            <Skeleton
              variant="rectangular"
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
              }}
            />
          )}
          <CardMedia
            component="img"
            image={outfit.imageUrl}
            alt={outfit.title || outfit.description || "Outfit image"}
            onLoad={() => setIsImageLoaded(true)}
            onError={() => {setIsImageLoaded(true);
            }}
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              display: isImageLoaded ? "block" : "none",
            }}
          />

          {/* Event type chip */}
          <Chip
            label={outfit.eventType || outfit.metadata?.eventType?.[0] || "casual"}
            size="small"
            color="primary"
            sx={{
              position: "absolute",
              top: 8,
              left: 8,
              backgroundColor: "rgba(46, 125, 50, 0.85)",
              color: "white",
              pointerEvents: "none",
            }}
          />

          {/* Style score badge */}
          <Box sx={{ position: "absolute", top: 8, right: 8, pointerEvents: "none" }}>
            <StyleScoreBadge
              score={outfit.ratings?.averageRating || 0}
              size="small"
            />
          </Box>

          {/* Quick action overlay — only rendered when hovered (desktop only) */}
          {isHovered && (
            <QuickActionOverlay
              outfitId={outfit.id}
              onRate={(rating) => handleVibeReact('🔥')}
              onLike={() => handleLikeChange(!isLiked)}
              onSave={handleSaveToggle}
              onShare={() => {}}
              visible={isHovered}
              isLiked={isLiked}
              isSaved={isSaved}
              currentRating={Math.round(outfit.ratings?.averageRating || 0)}
              disabled={false}
            />
          )}

          {/* User avatar */}
          {showUserInfo && (
            <Tooltip title={`Uploaded by ${userDisplayInfo.displayName}`}>
              <IconButton
                onClick={handleUserClick}
                sx={{
                  position: "absolute",
                  bottom: 4,
                  right: 4,
                  padding: 0,
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  },
                }}
              >
                {userDisplayInfo.isLoading ? (
                  <Skeleton
                    variant="circular"
                    width={32}
                    height={32}
                    sx={{ border: "2px solid white" }}
                  />
                ) : (
                  <Avatar
                    src={userDisplayInfo.profilePicture}
                    sx={{
                      width: 32,
                      height: 32,
                      border: "2px solid white",
                      cursor: "pointer",
                      "@media (prefers-reduced-motion: no-preference)": {
                        transition: "transform 0.2s ease-in-out",
                        "&:hover": {
                          transform: "scale(1.1)",
                        },
                      },
                    }}
                  >
                    {userDisplayInfo.profilePicture ? null : (
                      <PersonIcon sx={{ fontSize: 18 }} />
                    )}
                  </Avatar>
                )}
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <CardContent sx={{ flexGrow: 1 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <Typography variant="subtitle1" component="h3" noWrap>
              {outfit.title || outfit.description || "Untitled Outfit"}
            </Typography>
          </Box>

          {/* User information section */}
          {showUserInfo && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mt: 0.5,
                cursor: "pointer",
                "&:hover": {
                  "& .user-name": {
                    textDecoration: "underline",
                  },
                },
              }}
              onClick={handleUserClick}
            >
              {userDisplayInfo.isLoading ? (
                <>
                  <Skeleton variant="circular" width={20} height={20} sx={{ mr: 1 }} />
                  <Skeleton variant="text" width={80} height={16} />
                </>
              ) : (
                <>
                  <Avatar
                    src={userDisplayInfo.profilePicture}
                    sx={{
                      width: 20,
                      height: 20,
                      mr: 1,
                      fontSize: "0.7rem",
                    }}
                  >
                    {userDisplayInfo.profilePicture ? null : (
                      <PersonIcon sx={{ fontSize: 12 }} />
                    )}
                  </Avatar>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    className="user-name"
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 500,
                    }}
                  >
                    {userDisplayInfo.displayName}
                  </Typography>
                </>
              )}
            </Box>
          )}

          {/* AI Analysis Description (priority) or regular description */}
          {(outfit.aiAnalysis?.description || outfit.description) && (
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                mt: 0.5,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {outfit.aiAnalysis?.description || outfit.description}
            </Typography>
          )}

          {/* AI Analysis Badge */}
          {outfit.aiAnalysis && (
            <Chip
              label="AI Analyzed"
              size="small"
              color="secondary"
              sx={{ 
                mt: 0.5, 
                fontSize: '0.65rem', 
                height: '18px',
                backgroundColor: 'rgba(156, 39, 176, 0.1)',
                color: 'rgba(156, 39, 176, 0.8)',
                "@media (prefers-reduced-motion: no-preference)": {
                  transition: "box-shadow 300ms ease",
                  "&:hover": {
                    boxShadow: "0 0 12px rgba(187, 134, 252, 0.6)",
                  },
                },
              }}
            />
          )}

          {/* Tags */}
          {outfit.tags && outfit.tags.length > 0 && (
            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {outfit.tags.slice(0, 3).map((tag, index) => (
                <Chip
                  key={index}
                  label={tag}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem', height: '20px' }}
                />
              ))}
              {outfit.tags.length > 3 && (
                <Chip
                  label={`+${outfit.tags.length - 3}`}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem', height: '20px' }}
                />
              )}
            </Box>
          )}

          {/* Clothing Items Count */}
          {outfit.clothingItems && outfit.clothingItems.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {outfit.clothingItems.length} clothing item{outfit.clothingItems.length !== 1 ? 's' : ''}
            </Typography>
          )}

          {/* Affiliate links for tagged clothing items */}
          {affiliateData && affiliateData.length > 0 && outfit.clothingItems && (
            <Box sx={{ mt: 0.75, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {affiliateData.map((aff) => {
                const item = outfit.clothingItems[aff.itemIndex];
                if (!item) return null;
                return (
                  <Box key={aff.itemIndex} onClick={handleActionClick}>
                    <AffiliateLink item={item} affiliate={aff} />
                  </Box>
                );
              })}
            </Box>
          )}

          <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
            <StarRating
              value={outfit.ratings?.averageRating || 0}
              readOnly
              size="small"
              precision={0.5}
              showLabel={false}
            />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              ({outfit.ratings?.totalRatings || 0})
            </Typography>
          </Box>

          {/* Avatar cluster — social proof */}
          {totalRaterCount > 0 && (
            <Box sx={{ mt: 0.75 }}>
              <AvatarCluster raters={recentRaters} totalCount={totalRaterCount} />
            </Box>
          )}

          {/* Vibe Check Reactions */}
          <Box
            onClick={handleActionClick}
            sx={{ mt: 1 }}
          >
            <VibeCheckBar
              outfitId={outfit.id}
              reactions={localReactions}
              userReaction={localUserReaction}
              onReact={handleVibeReact}
              disabled={false}
            />
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {formatDate(outfit.createdAt || outfit.updatedAt)}
          </Typography>
        </CardContent>
      </CardActionArea>

      <CardActions sx={{ justifyContent: "space-between", px: 2, py: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box onClick={handleActionClick} sx={{
            "@media (prefers-reduced-motion: no-preference)": {
              "& .MuiIconButton-root:active": {
                animation: "likeButtonPop 300ms ease-in-out",
              },
              "@keyframes likeButtonPop": {
                "0%": { transform: "scale(1)" },
                "50%": { transform: "scale(1.3)" },
                "100%": { transform: "scale(1)" },
              },
            },
          }}>
            <LikeButton
              outfitId={outfit.id}
              initialLikeCount={outfit.likes || 0}
              initialLiked={isLiked}
              size="small"
              showCount={true}
              onLikeChange={handleLikeChange}
            />
          </Box>
          
          <Box onClick={handleActionClick}>
            <TryOnButton
              outfitId={outfit.id}
              outfitImageUrl={outfit.imageUrl}
              size="small"
              onTryOnStart={handleTryOnStart}
              onTryOnComplete={handleTryOnComplete}
              onTryOnError={handleTryOnError}
              onOpenModal={handleOpenTryOnModal}
            />
          </Box>
        </Box>

        <Box onClick={handleActionClick}>
          <ShareMenu outfitId={outfit.id} size="small" />
        </Box>
      </CardActions>

      {/* Try-On Modal */}
      {tryOnSessionId && (
        <TryOnModal
          isOpen={tryOnModalOpen}
          sessionId={tryOnSessionId}
          onClose={handleCloseTryOnModal}
          onDownload={handleDownloadTryOnResult}
          outfit={outfit}
        />
      )}
    </Card>
  );
};

export default OutfitCard;
